import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  EMPTY,
  Observable,
  catchError,
  concatWith,
  forkJoin,
  ignoreElements,
  map,
  take,
  tap,
  throwError,
} from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { SKIP_ERROR_TOAST } from './http/http-context-tokens';
import { NotificationService } from './services/notification.service';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const transloco = inject(TranslocoService);

  if (req.url.includes('/auth') || req.context.get(SKIP_ERROR_TOAST)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      console.error('Caught by HttpErrorInterceptor:', error);

      const apiCode: number | undefined = error.error?.error?.code;
      if (!(error.error instanceof ErrorEvent) && apiCode !== undefined && apiCode !== null) {
        return throwError(() => error);
      }

      if (error.status === 401) {
        return throwError(() => error);
      }

      // selectTranslate waits for the active language file: a request failing on the first load
      // can arrive before it, and a synchronous translate() would toast the raw key.
      let errorMessage$: Observable<string>;
      if (error.error instanceof ErrorEvent || error.status === 0) {
        // status 0 = request never reached a server (offline, CORS, timeout) — same message as
        // an ErrorEvent, and skips building an `errors.http0` key that will never be translated.
        errorMessage$ = transloco.selectTranslate('errors.network');
      } else {
        const httpKey = `errors.http${error.status}`;
        errorMessage$ = forkJoin([
          transloco.selectTranslate(httpKey).pipe(take(1)),
          transloco.selectTranslate('errors.httpDefault', { status: error.status }).pipe(take(1)),
        ]).pipe(map(([specific, fallback]) => (specific !== httpKey ? specific : fallback)));
      }

      // A language file that fails to load costs the toast, never the original error.
      return errorMessage$.pipe(
        take(1),
        tap((message) => notificationService.show(message)),
        catchError(() => EMPTY),
        ignoreElements(),
        concatWith(throwError(() => error)),
      );
    }),
  );
};
